import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { updateClient } from "@/features/clients/api";
import { MemberMultiSelect } from "@/features/clients/components/member-multi-select";
import type { Client } from "@/features/clients/types";
import { listMembers, type Member } from "@/features/team/api";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

function initials(name: string) {
  return (
    name
      .split(" ")
      .map((p) => p[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

/** One person row: initial-avatar tile + name + role, per the mockup. */
function MemberRow({
  name,
  role,
  tone,
}: {
  name: string;
  role: string;
  tone: "blue" | "amber";
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={cn(
          "font-heading flex size-9 shrink-0 items-center justify-center rounded-[11px] text-xs font-bold",
          tone === "blue" ? "bg-accent text-info" : "bg-warning-soft text-warning",
        )}
      >
        {initials(name)}
      </span>
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="truncate font-semibold">{name}</span>
        <span className="text-muted-foreground text-xs">{role}</span>
      </span>
    </div>
  );
}

/**
 * Client-screen team card: shows the team members + practitioner on this client
 * as avatar rows (Calm Studio "Team" mockup) and lets staff add/remove assigned
 * members from the firm's active members. Saves by PATCHing the full
 * `assigned_member_ids` replace-set.
 */
export function ClientTeamCard({
  client,
  onClientUpdated,
}: {
  client: Client;
  onClientUpdated: (client: Client) => void;
}) {
  const [members, setMembers] = useState<Member[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    listMembers()
      .then((list) => active && setMembers(list.filter((m) => m.status === "active")))
      .catch(() => active && setMembers([]));
    return () => {
      active = false;
    };
  }, []);

  const assigned = client.assignedMembers ?? [];
  const nsiAssigned = client.nsiAssignedMembers ?? [];
  const selectedIds = assigned.map((m) => m.id);
  const practitioners =
    client.practitioners?.length
      ? client.practitioners
      : client.practitioner
        ? [{ id: "primary", name: client.practitioner }]
        : [];

  async function handleChange(ids: string[]) {
    setSaving(true);
    try {
      const updated = await updateClient(client.id, { assigned_member_ids: ids });
      onClientUpdated(updated);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update team members.");
    } finally {
      setSaving(false);
    }
  }

  const hasPeople = assigned.length > 0 || practitioners.length > 0;

  return (
    <Card className="gap-4 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Team</h2>
        <MemberMultiSelect
          members={members}
          selected={selectedIds}
          onChange={(ids) => void handleChange(ids)}
          disabled={saving}
          placeholder="Manage"
        />
      </div>
      {!hasPeople ? (
        <p className="text-muted-foreground text-sm">No team members assigned.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {assigned.map((m, i) => (
            <MemberRow
              key={m.id}
              name={m.name}
              role={i === 0 ? "Assigned · case owner" : "Assigned"}
              tone="blue"
            />
          ))}
          {practitioners.map((p) => (
            <MemberRow key={p.id} name={p.name} role="Practitioner" tone="amber" />
          ))}
        </div>
      )}
      {nsiAssigned.length > 0 && (
        <div className="flex flex-col gap-2 border-t pt-4">
          <p className="text-muted-foreground text-xs font-medium">NSI-assigned</p>
          <div className="flex flex-wrap gap-1">
            {nsiAssigned.map((m) => (
              <Badge key={m.id} variant="outline" className="gap-1">
                <span className="text-muted-foreground">{m.bp_code ? `NSI · ${m.bp_code}` : "NSI"}</span>
                {m.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
